/**
 * Auth Logic Layer — Session management
 *
 * Owns: logout, refreshSession (single-flight), switchAccount,
 *       abortLoginAndClearSession, storage helpers, and "remember me" cookie.
 *
 * Rules:
 *  - No React, no navigate(), no notifications.
 *  - Updates store via getState()/setState() and localStorage/sessionStorage.
 *  - Returns outcome objects so the UI caller can decide navigation.
 */
import useAuthStore from "../stores/useAuthStore";
import { refreshTokenService } from "../api/services/authServices";
import { getCookie, setCookie, deleteCookie } from "../utils/cookies";
import { clearSentryUser } from "../observability/sentry";

const SESSION_REMEMBERED_NAME = "session_remembered";
const SESSION_REMEMBERED_DAYS = 30;

// ─── Remember me (cookie-backed) ───────────────────────────────────────────

/**
 * Whether the user previously chose "Remember me". Used to pre-check the checkbox.
 * @returns {boolean}
 */
export function getSessionRemembered() {
  return getCookie(SESSION_REMEMBERED_NAME) === "true";
}

/**
 * Persist "Remember me" for the next 30 days. Call after successful login when user checked the box.
 */
export function setSessionRemembered() {
  setCookie(SESSION_REMEMBERED_NAME, "true", SESSION_REMEMBERED_DAYS);
}

/**
 * Clear the "Remember me" cookie. Call when user unchecks the box or when flow should not remember.
 */
export function clearSessionRemembered() {
  deleteCookie(SESSION_REMEMBERED_NAME);
}

// ─── Single-flight refresh ──────────────────────────────────────────────────
let inflightRefresh = null;

/**
 * Refresh the current session token.
 * Single-flight: concurrent callers share the same in-flight promise.
 *
 * @returns {Promise<{ ok: true, token: string } | { ok: false, error: any }>}
 */
export async function refreshSession() {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    try {
      const sessionId = localStorage.getItem("sessionId");
      const currentToken = useAuthStore.getState().authToken;

      if (!sessionId || !currentToken) {
        logout();
        return { ok: false, error: "missing_session" };
      }

      const data = await refreshTokenService(sessionId, currentToken);
      const newToken = data.access_token;

      localStorage.setItem("token", newToken);
      useAuthStore.getState().setAuthToken(newToken);

      return { ok: true, token: newToken };
    } catch (error) {
      console.error("Session refresh failed:", error);
      logout({ setExpired: true });
      return { ok: false, error };
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

// ─── Logout ─────────────────────────────────────────────────────────────────

/**
 * Full logout: clears store + storage.
 * @param {{ setExpired?: boolean }} options
 * @returns {{ redirectPath: string }}
 */
export function logout({ setExpired = false } = {}) {
  const store = useAuthStore.getState();

  clearSentryUser();
  store.clearSession();

  if (setExpired) {
    store.setSessionExpired(true);
  }

  return { redirectPath: "/login" };
}

// ─── Abort login (back to login) ────────────────────────────────────────────

/**
 * Clears all login-related state (pending tokens, session, store).
 * @returns {{ redirectPath: string }}
 */
export function abortLoginAndClearSession() {
  clearSentryUser();
  useAuthStore.getState().clearSession();

  return { redirectPath: "/login" };
}

// ─── Switch demo role / impersonation ───────────────────────────────────────

/**
 * Write a new access token for role-switch and leave app in authenticated state.
 * Caller should do a full-page reload or SPA-navigate after.
 *
 * @param {string} accessToken - The access_token from the account that is being switched into
 */
export function switchAccount(accessToken) {
  const store = useAuthStore.getState();
  const currentInitial = localStorage.getItem("initial_token");

  localStorage.setItem("token", accessToken);
  store.setAuthToken(accessToken);

  if (currentInitial) store.setInitialAuthToken(currentInitial);
}

// ─── Storage helpers (internal, exported for login/policy modules) ──────────

const PENDING_KEYS = [
  "pending_token",
  "pending_initial_token",
];

/**
 * Write pending tokens from a login response into localStorage.
 * Does NOT touch the store's authToken so PrivateRoute still blocks.
 */
export function writePendingTokens(response, landingPath) {
  localStorage.setItem("sessionId", response?.session?._id ?? "");
  localStorage.setItem("pending_initial_token", response?.access_token ?? "");
  localStorage.setItem("pending_token", response?.access_token ?? "");
  sessionStorage.setItem("postLoginRedirect", landingPath);
}

/**
 * Promote pending tokens to real tokens in localStorage + store.
 * Clears pending keys. Returns the redirect path the user should land on.
 *
 * @returns {{ promoted: boolean, redirectPath: string }}
 */
export function promotePendingSession() {
  const pendingToken = localStorage.getItem("pending_token");
  const pendingInitial = localStorage.getItem("pending_initial_token");

  if (!pendingToken) {
    // Already promoted (or no session). If a real token exists, point to the
    // app instead of the login page and clear the stale policy_pending status.
    const existingToken = localStorage.getItem("token");
    if (existingToken) {
      const store = useAuthStore.getState();
      if (store.authBootstrapStatus === "policy_pending") {
        store.setAuthBootstrapStatus("bootstrapping");
      }
      return {
        promoted: false,
        redirectPath: sessionStorage.getItem("postLoginRedirect") || "/dashboard",
      };
    }
    return { promoted: false, redirectPath: "/login" };
  }

  localStorage.setItem("token", pendingToken);
  localStorage.setItem("initial_token", pendingInitial || pendingToken);
  PENDING_KEYS.forEach((k) => localStorage.removeItem(k));

  const store = useAuthStore.getState();
  // Transition away from policy_pending so the AuthStoreHydrator's authToken
  // effect can proceed to fetch user data instead of returning early.
  store.setAuthBootstrapStatus("bootstrapping");
  store.setInitialAuthToken(pendingInitial || pendingToken);
  store.setAuthToken(pendingToken);

  const redirectPath =
    sessionStorage.getItem("postLoginRedirect") || "/dashboard";
  sessionStorage.removeItem("postLoginRedirect");

  return { promoted: true, redirectPath };
}
